import React, { useState, useEffect, useCallback } from 'react';
import { CreditCard, ExternalLink, AlertTriangle, Loader2, MapPin, Star, X, CalendarX, RotateCcw, Shield, ArrowUpRight } from 'lucide-react';
import { useOrg } from '../../hooks/useOrg';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { api } from '../../lib/api';
import { OWARE_URL } from '../../lib/oware';

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtMoney(cents) {
  if (cents == null) return '—';
  return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 0 });
}

const STATUS_LABEL = {
  active:       { text: 'Active',          tone: 'text-success' },
  trialing:     { text: 'In trial',        tone: 'text-accent-light' },
  past_due:     { text: 'Past due',        tone: 'text-warning' },
  paused:       { text: 'Trial ended — add a card', tone: 'text-warning' },
  incomplete:   { text: 'Pending payment', tone: 'text-warning' },
  canceled:     { text: 'Canceled',        tone: 'text-muted-c' },
  unpaid:       { text: 'Unpaid',          tone: 'text-danger' },
};

export default function BillingSettings() {
  const { currentOrg, currentMembership } = useOrg();
  const { subscription, billedLocations, loading, refresh } = useSubscription();
  const [modeBusy, setModeBusy] = useState(null);
  const [modeError, setModeError] = useState('');
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState(null);
  // Cancel/Resume state. `confirmingCancel` toggles the confirmation modal;
  // `acting` covers both cancel and resume API calls.
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [acting, setActing] = useState(false);
  const [actionError, setActionError] = useState(null);

  const isPaidOrg = currentOrg?.type === 'medical_practice';
  // The founder (whoever created the org) is the de-facto owner. The user_role
  // enum doesn't currently include 'owner'; is_primary_org is the canonical
  // founder flag — set by both the legacy create_organization_with_owner RPC
  // and the new checkoutCompletion service.
  const isOwner = !!currentMembership?.is_primary_org;

  const openPortal = async (body = {}) => {
    setOpening(true);
    setError(null);
    try {
      const { url } = await api.createPortalSession(currentOrg.id, body);
      window.location.href = url;
    } catch (err) {
      setError(err.message);
      setOpening(false);
    }
  };

  // Trial ended without a card → Stripe paused the subscription. Add a card
  // (portal 'add_card' flow comes back here with ?reactivate=1), then resume.
  const [reactivating, setReactivating] = useState(false);
  const [reactivated, setReactivated] = useState(false);
  const reactivate = useCallback(async () => {
    setReactivating(true); setActionError(null);
    try {
      const out = await api.reactivateSubscription(currentOrg.id);
      if (out.ok) { setReactivated(true); await refresh(); }
    } catch (err) {
      setActionError(err.code === 'no_payment_method' ? 'No card on file yet — add one first, then reactivate.' : err.message);
    } finally { setReactivating(false); }
  }, [currentOrg?.id, refresh]);
  useEffect(() => {
    if (!currentOrg?.id || subscription?.status !== 'paused') return;
    const q = new URLSearchParams(window.location.search);
    if (q.get('reactivate') === '1') {
      q.delete('reactivate');
      window.history.replaceState({}, '', `${window.location.pathname}?${q}`);
      reactivate();
    }
  }, [currentOrg?.id, subscription?.status, reactivate]);

  // Cancels the Stripe subscription at the end of the current paid period.
  // Real Stripe call on the backend (cancel_at_period_end=true); the
  // customer.subscription.updated webhook reconciles, but we also refresh
  // immediately so the UI flips to the "scheduled to cancel" state.
  const handleCancel = async () => {
    setActing(true); setActionError(null);
    try {
      await api.cancelSubscription(currentOrg.id);
      await refresh();
      setConfirmingCancel(false);
    } catch (err) {
      setActionError(err.message);
    } finally {
      setActing(false);
    }
  };

  // Undoes a scheduled cancellation before the period end.
  const handleResume = async () => {
    setActing(true); setActionError(null);
    try {
      await api.resumeSubscription(currentOrg.id);
      await refresh();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setActing(false);
    }
  };

  if (!isPaidOrg) {
    return (
      <div className="p-6">
        <div className="rounded-[10px] border border-theme p-5 bg-tertiary text-[13px] text-muted-c">
          Billing isn't applicable to law firm accounts. MediLink is free for referring attorneys.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-[13px] text-muted-c">
        <Loader2 size={14} className="animate-spin" /> Loading billing details…
      </div>
    );
  }

  const status = subscription?.status;
  const statusMeta = status ? STATUS_LABEL[status] || { text: status, tone: 'text-muted-c' } : null;
  const unitAmount = subscription?.unit_amount ?? null;
  const quantity = subscription?.quantity ?? 1;
  const locationCount = billedLocations.length || quantity;
  const paidLocations = billedLocations.filter(l => l.billing_mode !== 'free_until_referral');
  const freeLocations = billedLocations.filter(l => l.billing_mode === 'free_until_referral');
  // Billed offices = the ones that received a case. With no location rows
  // loaded fall back to what Stripe says.
  const billedCount = billedLocations.length ? paidLocations.length : quantity;
  const monthlyTotal = unitAmount != null ? unitAmount * billedCount : null;
  const multi = billedLocations.length > 1;

  // Pay-per-location only where referrals land (065, rule of 2026-11-03):
  // every office is free until it receives a case; accepting a referral
  // there is what adds it to the subscription.
  const setMode = async (loc, mode) => {
    setModeBusy(loc.id); setModeError('');
    try { await api.updateLocation(currentOrg.id, loc.id, { billing_mode: mode }); await refresh(); }
    catch (err) { setModeError(err.message); }
    finally { setModeBusy(null); }
  };

  return (
    <div className="p-6 max-w-[640px]">
      <h2 className="text-[16px] font-bold text-primary-c mb-1">Billing</h2>
      <p className="text-[12px] text-muted-c mb-5">Manage your MediLink Pro subscription. Each location is billed at the same rate.</p>

      {!subscription && (
        <div className="rounded-[10px] border border-warning/30 bg-warning/10 p-4 mb-4 flex items-start gap-3">
          <AlertTriangle size={16} className="text-warning flex-shrink-0 mt-0.5" />
          <div className="text-[12px] text-primary-c">
            <div className="font-semibold mb-1">No active subscription</div>
            <div className="text-muted-c">Your account is not currently active. Subscribe to unlock the platform.</div>
          </div>
        </div>
      )}

      {subscription && (
        <div className="rounded-[10px] border border-theme overflow-hidden mb-4">
          <div className="p-5 border-b border-theme flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-[36px] h-[36px] rounded-[10px] bg-accent-glow flex items-center justify-center text-accent-light">
                <CreditCard size={16} />
              </div>
              <div>
                <div className="text-[13px] font-semibold text-primary-c">MediLink Pro</div>
                <div className="text-[11px] text-muted-c">
                  {unitAmount != null ? `${fmtMoney(unitAmount)} per location / month` : 'Per-location pricing'}
                </div>
              </div>
            </div>
            <span className={`text-[11px] font-semibold ${statusMeta.tone}`}>{statusMeta.text}</span>
          </div>

          {/* Per-location breakdown */}
          <div className="px-5 py-4 border-b border-theme">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[12px] font-semibold text-primary-c">
                Locations ({locationCount}) · {paidLocations.length} billed{freeLocations.length ? ` · ${freeLocations.length} free until a referral` : ''}
              </div>
              {unitAmount != null && (
                <div className="text-[12px] text-muted-c">
                  {fmtMoney(unitAmount)} × {billedCount} = <span className="font-semibold text-primary-c">{fmtMoney(unitAmount * billedCount)}/mo</span>
                </div>
              )}
            </div>
            {multi && (
              <p className="text-[11px] text-muted-c mb-3">
                {status === 'trialing'
                  ? 'Every location is free until it receives a case. A location joins your subscription only when you accept a referral there.'
                  : 'A free location stays fully usable and joins your subscription only when you accept a referral there.'}
              </p>
            )}
            {modeError && <div className="mb-2 text-[11px] text-danger">{modeError}</div>}
            {billedLocations.length > 0 ? (
              <ul className="space-y-1.5">
                {billedLocations.map(l => {
                  const free = l.billing_mode === 'free_until_referral';
                  const pending = free && l.billing_starts_at;
                  const busy = modeBusy === l.id;
                  return (
                    <li key={l.id} className="flex items-center gap-2 text-[12px] text-secondary-c flex-wrap">
                      <MapPin size={11} strokeWidth={2.2} className="text-accent-light flex-shrink-0" />
                      <span className="truncate">{l.name}</span>
                      {l.is_primary && (
                        <span className="text-[10px] uppercase tracking-wide text-muted-c flex items-center gap-1">
                          <Star size={9} strokeWidth={2.5} fill="currentColor" /> Primary
                        </span>
                      )}
                      <span className="ml-auto flex items-center gap-2">
                        {pending ? (
                          <span className="text-[10.5px] font-semibold text-warning">Billed from {fmtDate(l.billing_starts_at)} — referred patient booked</span>
                        ) : free ? (
                          <span className="text-[10.5px] font-semibold text-muted-c">Free until it receives a case</span>
                        ) : (
                          <span className="text-[10.5px] font-semibold text-accent-light">{status === 'trialing' ? 'In your trial' : status === 'paused' ? 'Billed once a card is added' : 'Billed'}{l.billing_triggered_at ? ' · via referral' : ''}</span>
                        )}
                        {multi && !l.billing_triggered_at && !pending && (
                          <button onClick={() => setMode(l, free ? 'paid' : 'free_until_referral')} disabled={busy}
                            className="text-[10.5px] font-semibold text-accent-light hover:text-accent underline-offset-2 hover:underline disabled:opacity-50">
                            {busy ? '…' : free ? 'Pay for this location' : 'Make free until a referral'}
                          </button>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="text-[12px] text-muted-c">No active locations.</div>
            )}
            {quantity !== billedCount && (
              <div className="mt-3 text-[11px] text-warning">
                Stripe quantity ({quantity}) differs from paid locations ({paidLocations.length}). The next subscription update will resync.
              </div>
            )}
          </div>

          <div className="p-5 grid grid-cols-2 gap-4 text-[12px]">
            <div>
              <div className="text-muted-c mb-1">Current period</div>
              <div className="text-primary-c">{fmtDate(subscription.current_period_start)} → {fmtDate(subscription.current_period_end)}</div>
            </div>
            <div>
              <div className="text-muted-c mb-1">Next renewal</div>
              <div className="text-primary-c">
                {subscription.cancel_at_period_end
                  ? <span className="text-warning">Cancels on {fmtDate(subscription.current_period_end)}</span>
                  : fmtDate(subscription.current_period_end)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scheduled-cancellation banner — appears once cancel is in flight.
          Includes the "Keep subscription" (resume) action for owners. */}
      {subscription?.cancel_at_period_end && (
        <div className="rounded-[12px] border border-warning/40 bg-warning/10 p-4 mb-4 flex items-start gap-3">
          <CalendarX size={18} className="text-warning flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-[12px] text-primary-c">
            <div className="font-semibold mb-1">Subscription scheduled to end</div>
            <div className="text-muted-c">
              You'll keep access until <strong className="text-primary-c">{fmtDate(subscription.current_period_end)}</strong>. After that, MediLink Pro will turn off and you won't be charged again.
            </div>
            {isOwner && (
              <button
                onClick={handleResume}
                disabled={acting}
                className="mt-3 px-3 py-2 rounded-[10px] text-[12px] font-semibold text-accent-light bg-accent-glow border border-strong hover:bg-hover-surface transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {acting ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
                {acting ? 'Resuming…' : 'Keep subscription'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* OWARE perk tile — included with every medical_practice subscription.
          Subscribers are auto-recognised by the Statra-hosted onboarding URL,
          so this tile just explains the perk and links them out. No codes,
          copy/paste, or extra credentials. */}
      {subscription && (
        <div className="rounded-[10px] border border-theme overflow-hidden mb-4">
          <div className="p-5 flex items-start gap-4">
            <div className="w-[36px] h-[36px] rounded-[10px] bg-accent-glow flex items-center justify-center text-accent-light flex-shrink-0">
              <Shield size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="text-[13px] font-semibold text-primary-c">OWARE</div>
                <span className="text-[10px] uppercase tracking-wide font-semibold text-accent-light bg-accent-glow px-1.5 py-0.5 rounded-[6px]">
                  Included
                </span>
              </div>
              <p className="text-[12px] text-muted-c mb-3 leading-relaxed">
                Financial health and fraud monitoring for your practice, bundled
                free with your subscription. OWARE connects to your accounting
                and banking once, then runs daily snapshots, a weekly owner
                digest, and real-time alerts when something looks off.
              </p>

              <a
                href={OWARE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow hover:opacity-95 transition-all"
              >
                Set up your OWARE account <ArrowUpRight size={13} strokeWidth={2.5} />
              </a>
              <p className="text-[11px] text-muted-c mt-2">
                Setup takes just a few minutes on secure.oware.com.
              </p>
            </div>
          </div>
        </div>
      )}

      {(error || actionError) && (
        <div className="mb-3 p-3 rounded-[10px] bg-danger/10 border border-danger/30 text-[12px] text-danger">
          {error || actionError}
        </div>
      )}

      {status === 'paused' && !reactivated && (
        <div className="mb-4 rounded-[12px] border border-warning/40 bg-warning-soft px-4 py-3.5">
          <div className="text-[13px] font-semibold text-primary-c mb-1">Your free trial has ended</div>
          <p className="text-[12px] text-muted-c mb-3">
            Referrals still reach your inbox, but you need a payment method on file to accept them. Add a card, and your subscription restarts today
            {monthlyTotal != null ? ` at ${fmtMoney(monthlyTotal)}/mo` : ''}.
          </p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => openPortal({ flow: 'add_card' })} disabled={opening || reactivating} className="px-4 py-2.5 rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow flex items-center gap-2 disabled:opacity-60">
              {opening ? <Loader2 size={13} className="animate-spin" /> : <CreditCard size={13} />} Add a card & reactivate
            </button>
            <button onClick={reactivate} disabled={reactivating || opening} className="px-4 py-2.5 rounded-[10px] text-[12px] font-semibold text-accent-light bg-accent-glow border border-strong hover:bg-hover-surface flex items-center gap-2 disabled:opacity-60">
              {reactivating ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />} I already added a card — reactivate
            </button>
          </div>
        </div>
      )}
      {reactivated && (
        <div className="mb-4 rounded-[12px] border border-success/40 bg-success-soft px-4 py-3 text-[12.5px] text-primary-c">
          <span className="font-semibold">Subscription reactivated.</span> You can accept referrals again.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => openPortal()}
          disabled={opening || !subscription}
          className="px-4 py-2.5 rounded-[10px] text-[12px] font-semibold text-accent-light bg-accent-glow border border-strong hover:bg-hover-surface transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {opening ? <Loader2 size={13} className="animate-spin" /> : <ExternalLink size={13} />}
          {opening ? 'Opening Stripe portal…' : 'Manage subscription'}
        </button>

        {isOwner && subscription && !subscription.cancel_at_period_end && (
          <button
            onClick={() => { setActionError(null); setConfirmingCancel(true); }}
            disabled={acting}
            className="px-4 py-2.5 rounded-[10px] text-[12px] font-semibold text-danger border border-danger/30 hover:bg-danger/10 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <CalendarX size={13} />
            Cancel subscription
          </button>
        )}
      </div>
      <p className="text-[11px] text-muted-c mt-2">
        Manage payment method and view invoices in the Stripe-hosted portal. Cancel directly here.
      </p>

      <div className="mt-6">
        <button onClick={refresh} className="text-[11px] text-muted-c hover:text-primary-c">Refresh status</button>
      </div>

      {/* Confirmation modal — calls the real Stripe API on confirm
          (subscriptions.update cancel_at_period_end=true). Webhook reconciles
          while we also refresh inline. */}
      {confirmingCancel && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center px-4 bg-overlay backdrop-blur-sm"
          onClick={() => !acting && setConfirmingCancel(false)}
        >
          <div
            className="bg-elevated border border-theme rounded-2xl shadow-2xl w-full max-w-md"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-theme">
              <h3 className="text-[16px] font-bold text-primary-c">Cancel subscription?</h3>
              <button
                onClick={() => !acting && setConfirmingCancel(false)}
                disabled={acting}
                className="w-8 h-8 rounded-[8px] flex items-center justify-center text-muted-c hover:bg-hover-surface transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <X size={16} strokeWidth={2.2} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="rounded-[12px] border border-warning/40 bg-warning/10 p-4 flex items-start gap-3">
                <AlertTriangle size={18} className="text-warning flex-shrink-0 mt-0.5" />
                <div className="text-[13px] text-primary-c">
                  {subscription?.status === 'trialing' ? (
                    <>Your free trial ends on <strong>{fmtDate(subscription.current_period_end)}</strong>. Canceling now means you won't be charged when it ends, and access turns off then.</>
                  ) : (
                    <>You'll keep full access to MediLink Pro until <strong>{fmtDate(subscription?.current_period_end)}</strong>. After that, your subscription ends and you won't be charged again.</>
                  )}
                </div>
              </div>

              <div className="text-[12px] text-muted-c">
                You can resume any time before the end date.
              </div>

              {actionError && (
                <div className="px-4 py-3 rounded-[10px] bg-danger-soft text-danger text-[13px] font-medium">
                  {actionError}
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  onClick={() => setConfirmingCancel(false)}
                  disabled={acting}
                  className="px-4 py-2.5 rounded-[10px] text-[12px] font-semibold text-primary-c bg-tertiary border border-theme hover:bg-hover-surface transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Keep subscription
                </button>
                <button
                  onClick={handleCancel}
                  disabled={acting}
                  style={{ background: 'var(--danger)', color: '#fff' }}
                  className="px-4 py-2.5 rounded-[10px] text-[12px] font-semibold hover:opacity-90 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {acting && <Loader2 size={13} className="animate-spin" />}
                  {acting ? 'Canceling…' : 'Cancel subscription'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
